import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";
import {
  PositionInventory,
  PositionInventoryDetail,
  inventoryService,
} from "~/services/wms/inventoryService";
import type { WarehouseInventoryData } from "~/types/wms";

const WAREHOUSE_INVENTORY_KEY = "wms_warehouse_inventory";
const RACK_INVENTORY_KEY = "wms_rack_inventory";
const POSITION_INVENTORY_KEY = "wms_position_inventory";
const PRODUCT_INVENTORY_KEY = "wms_product_inventory";

/**
 * Hook to get all positions in a rack with their inventory data
 * Used for the visual rack grid display
 */
export function useRackInventory(rackId?: string) {
  return useQuery<PositionInventory[], Error>({
    queryKey: [RACK_INVENTORY_KEY, rackId],
    queryFn: () => inventoryService.getInventoryByRack(rackId!),
    enabled: !!rackId,
  });
}

/**
 * Hook to get detailed inventory for a single position
 * Used when clicking on a position in the grid
 */
export function usePositionInventory(positionId?: string) {
  return useQuery<PositionInventoryDetail, Error>({
    queryKey: [POSITION_INVENTORY_KEY, positionId],
    queryFn: () => inventoryService.getPositionInventory(positionId!),
    enabled: !!positionId,
  });
}

/**
 * Hook to get full warehouse inventory with hierarchy
 * Used for the full warehouse grid visualization
 */
export function useWarehouseInventory(warehouseId?: string) {
  return useQuery<WarehouseInventoryData, Error>({
    queryKey: [WAREHOUSE_INVENTORY_KEY, warehouseId],
    queryFn: () => inventoryService.getInventoryByWarehouse(warehouseId!),
    enabled: !!warehouseId,
  });
}

/**
 * Hook to get all positions with inventory for a specific product
 * Used for stock adjustment decrements - shows where product is stored
 * Returns inventory sorted by FEFO (earliest expiration first)
 */
export interface ProductPositionInventory {
  id: string;
  position_id: string;
  box_count: number;
  lot_number: string | null;
  expiration_date: string | null;
  position: {
    id: string;
    full_code: string;
    code: string;
    rack: {
      name: string;
      hallway: {
        warehouse_id: string;
        warehouse: { id: string; name: string; code: string } | null;
      } | null;
    } | null;
  } | null;
}

export function useProductPositionInventory(productId: string | undefined) {
  return useQuery<ProductPositionInventory[], Error>({
    queryKey: [PRODUCT_INVENTORY_KEY, productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wms_inventory")
        .select(
          `
          id,
          position_id,
          box_count,
          lot_number,
          expiration_date,
          position:wms_positions(id, full_code, code, rack:wms_racks(name, hallway:wms_hallways(warehouse_id, warehouse:wms_warehouses(id, name, code))))
        `
        )
        .eq("product_id", productId!)
        .gt("box_count", 0)
        .order("expiration_date", { ascending: true, nullsFirst: false });

      if (error) throw error;
      return (data || []) as ProductPositionInventory[];
    },
    enabled: !!productId,
  });
}

// Re-export types for convenience
export type { PositionInventory };
